import { hirschberg } from './lcs';

export type DiffOp =
	| { kind: 'match'; length: number; newIndex: number; oldIndex: number }
	| { from: number; insertAt: number; kind: 'insert'; to: number }
	| { from: number; kind: 'delete'; to: number }
	| {
			from: number;
			kind: 'replace';
			replaceFrom: number;
			replaceTo: number;
			to: number;
	  };

/**
 * Generic list diff. Emits a sequence of ops describing how to transform
 * `oldList` into `newList`. Mirrors `diffList` in `core/diff/Difference.kt`.
 *
 * Two predicates:
 *  - `strictEqual` (default `===`) is used for prefix/suffix exclusion AND as
 *    a preferred anchor inside the middle: pairs that compare strict-equal
 *    are locked first; same-type LCS only runs in the gaps between them.
 *  - `similarity` (default `strictEqual`) is the looser fallback for the
 *    inner LCS. Items that are merely similar (e.g. same node type, different
 *    content) get aligned as a Match op and the consumer is expected to
 *    recurse.
 *
 * Why anchor on strict-equal pairs before similarity LCS: with same-type-only
 * similarity, every paragraph matches every other paragraph from the LCS's
 * POV. When old has one more (or fewer) of a given block type than new —
 * e.g. a trailing empty paragraph the markdown round-trip dropped — multiple
 * length-K LCS alignments exist and Hirschberg's tie-break can pick one that
 * pairs a content-rich paragraph with an unrelated one, producing a wholesale
 * inline replace that destroys marks the inheritance rule can't recover
 * (annotations on the wrongly-paired old paragraph). Anchoring on strict
 * matches first prevents this: paragraphs whose content is already identical
 * (modulo ignored marks) are locked as match anchors, so the same-type LCS
 * only ever runs inside the small gaps between them.
 *
 * `maxRegions`: when >0, abort the LCS and collapse to a single Replace if
 * the alignment would produce more than that many match runs. Caps the
 * pathological case. Applied per gap; the anchor count is uncapped (typically
 * small).
 */
export function diffList<T>(
	oldList: readonly T[],
	newList: readonly T[],
	emit: (op: DiffOp) => void,
	similarity: (a: T, b: T) => boolean = (a, b) => a === b,
	maxRegions = 0,
	strictEqual: (a: T, b: T) => boolean = similarity,
): void {
	const oldSize = oldList.length;
	const newSize = newList.length;

	// Common prefix
	let prefix = 0;
	while (prefix < Math.min(oldSize, newSize) && strictEqual(oldList[prefix], newList[prefix])) {
		prefix++;
	}

	// Common suffix (only over the part not already consumed by prefix)
	let suffix = 0;
	while (
		suffix < Math.min(oldSize, newSize) - prefix &&
		strictEqual(oldList[oldSize - 1 - suffix], newList[newSize - 1 - suffix])
	) {
		suffix++;
	}

	if (prefix > 0) emit({ kind: 'match', oldIndex: 0, newIndex: 0, length: prefix });
	if (suffix > 0)
		emit({
			kind: 'match',
			oldIndex: oldSize - suffix,
			newIndex: newSize - suffix,
			length: suffix,
		});

	if (prefix === oldSize && prefix === newSize) return;

	const oldMidLo = prefix;
	const oldMidHi = oldSize - suffix;
	const newMidLo = prefix;
	const newMidHi = newSize - suffix;

	const oldMid = oldList.slice(oldMidLo, oldMidHi);
	const newMid = newList.slice(newMidLo, newMidHi);

	// Phase 1: lock strict-equal pairs as anchors.
	// If `strictEqual` is the same function reference as `similarity` (the
	// default), the anchor pass is degenerate with the LCS pass below; skip it.
	const anchors =
		strictEqual === similarity ? [] : (hirschberg(oldMid, newMid, strictEqual, 0) ?? []);

	// Phase 2: run same-type LCS inside each gap between anchors.
	// The result `lcsRelative` is the union of all anchor pairs and gap LCS
	// pairs, in increasing order, in coords relative to oldMid / newMid.
	let lcsRelative: Array<[number, number]> | null = [];
	let lastOldEnd = 0;
	let lastNewEnd = 0;
	const runGap = (oldLo: number, oldHi: number, newLo: number, newHi: number) => {
		if (lcsRelative === null) return;
		if (oldHi - oldLo === 0 || newHi - newLo === 0) return;
		const gapPairs = hirschberg(
			oldMid.slice(oldLo, oldHi),
			newMid.slice(newLo, newHi),
			similarity,
			maxRegions,
		);
		if (gapPairs === null) {
			lcsRelative = null;
			return;
		}
		for (const [i, j] of gapPairs) {
			lcsRelative.push([oldLo + i, newLo + j]);
		}
	};
	for (const [aOld, aNew] of anchors) {
		runGap(lastOldEnd, aOld, lastNewEnd, aNew);
		if (lcsRelative === null) break;
		lcsRelative.push([aOld, aNew]);
		lastOldEnd = aOld + 1;
		lastNewEnd = aNew + 1;
	}
	if (lcsRelative !== null) {
		runGap(lastOldEnd, oldMid.length, lastNewEnd, newMid.length);
	}

	// LCS aborted (too many regions in some gap) → collapse to a single replace
	// over the entire middle. Anchors are discarded here on purpose: once we've
	// decided to fall back to wholesale replace, partial anchoring would emit a
	// weird mix of small matches plus a giant replace around them.
	if (lcsRelative === null) {
		emit({
			kind: 'replace',
			replaceFrom: oldMidLo,
			replaceTo: oldMidHi,
			from: newMidLo,
			to: newMidHi,
		});
		return;
	}

	// Adjust LCS pairs back to absolute indices.
	const lcs: Array<[number, number]> = lcsRelative.map(([i, j]) => [i + prefix, j + prefix]);

	if (lcs.length === 0) {
		// No matches in the middle - one of insert/delete/replace
		if (oldMidHi - oldMidLo === 0) {
			emit({
				kind: 'insert',
				insertAt: oldMidHi,
				from: newMidLo,
				to: newMidHi,
			});
		} else if (newMidHi - newMidLo === 0) {
			emit({ kind: 'delete', from: oldMidLo, to: oldMidHi });
		} else {
			emit({
				kind: 'replace',
				replaceFrom: oldMidLo,
				replaceTo: oldMidHi,
				from: newMidLo,
				to: newMidHi,
			});
		}
		return;
	}

	// Walk old/new in tandem, emitting Match/Insert/Delete runs based on LCS pairs.
	let oldIdx = prefix;
	let newIdx = prefix;
	let lcsIdx = 0;

	const matched = () =>
		lcsIdx < lcs.length && oldIdx === lcs[lcsIdx][0] && newIdx === lcs[lcsIdx][1];

	const shouldInsert = () => {
		if (newIdx >= newSize) return false;
		if (lcsIdx < lcs.length) return newIdx < lcs[lcsIdx][1];
		return newSize - newIdx > oldSize - oldIdx;
	};
	const shouldDelete = () => {
		if (oldIdx >= oldSize) return false;
		if (lcsIdx < lcs.length) return oldIdx < lcs[lcsIdx][0];
		return oldSize - oldIdx > newSize - newIdx;
	};

	while (oldIdx + suffix < oldSize || newIdx + suffix < newSize) {
		if (matched()) {
			const oldStart = oldIdx;
			const newStart = newIdx;
			let len = 0;
			do {
				oldIdx++;
				newIdx++;
				lcsIdx++;
				len++;
			} while (matched());
			emit({ kind: 'match', oldIndex: oldStart, newIndex: newStart, length: len });
		} else if (shouldInsert()) {
			const start = newIdx;
			do newIdx++;
			while (shouldInsert());
			emit({ kind: 'insert', insertAt: oldIdx, from: start, to: newIdx });
		} else {
			const start = oldIdx;
			do oldIdx++;
			while (shouldDelete());
			emit({ kind: 'delete', from: start, to: oldIdx });
		}
	}
}
